import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { formatNumber } from '@angular/common';
import { HelpersService } from '@app/shared/services/helpers.service';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { catchError, EMPTY, first, map, merge, Observable, Subject, Subscription, switchMap, tap } from 'rxjs';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MtxDrawer } from '@ng-matero/extensions/drawer';
import {
  CourierInvoiceAuditDetail,
  FinanceService,
  InvoiceAuditSectionName,
  ReconAuditFailedRow,
} from '@app/pages/authenticated/pages/finance/finance.service';
import { AdminScopes, AdminUser, UserService } from '@app/shared/services/user.service';
import {
  Animations,
  DateFormats,
  DefaultTimezone,
  EDI_INVOICE_MODAL_TYPE,
  PanelWidths,
  SupportedReconProvider,
} from '@app/shared/constants';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { EdiInvoiceComponent } from '@app/pages/authenticated/components/edi-invoice/edi-invoice.component';
import {
  buildOutcomeMessage,
  buildReconciledEmptyState,
  getIdentifierLabel,
  getSectionNotice,
  getSectionState,
  getStatusCellText,
} from '../courier-invoice-outcome.rules';

interface CountTile {
  label: string;
  value: string;
  subLine: string | null;
  // The sub-line's source section could not be loaded (never read as zero).
  unavailable: boolean;
}

const PILL_CLASS_BY_STATUS: Record<string, string> = {
  pending: 'created',
  processing: 'in-transit',
  processed: 'delivered',
  processed_with_failures: 'attention',
};

@Component({
  standalone: false,
  selector: 'app-courier-invoice-shipments',
  templateUrl: './invoice-shipments.component.html',
  animations: [Animations.ExpandAnimation],
})
export class InvoiceShipmentsComponent implements OnInit, OnDestroy {
  public loading = true;
  public user: AdminUser;
  public invoiceName: string;
  public detail: CourierInvoiceAuditDetail | null = null;
  public DATE_FORMAT: string = DateFormats.DATE_YEAR;
  public defaultTimezone = DefaultTimezone;
  public canModifyFinance = false;
  public canViewUnknownTracking = false;

  public unknownColumns = ['createdAt', 'type', 'trackingNumber', 'reference', 'cost', 'reason', 'notes'];
  public failedColumns = ['identifier', 'invoiceNumber', 'amount', 'reason', 'attempts', 'lastFailedAt', 'state', 'rawRow'];
  public unknownDataSource = new MatTableDataSource<any>([]);
  public failedDataSource = new MatTableDataSource<ReconAuditFailedRow>([]);

  // The paginators sit inside tabs that are not rendered until selected.
  @ViewChild('unknownPaginator') set unknownPaginator(paginator: MatPaginator) {
    this.unknownDataSource.paginator = paginator ?? null;
  }
  @ViewChild('failedPaginator') set failedPaginator(paginator: MatPaginator) {
    this.failedDataSource.paginator = paginator ?? null;
  }

  // Computed once per loaded detail (not on every change detection).
  public tiles: CountTile[] = [];
  public unknownTrackingQueryParams: Record<string, string> = {};

  private routeSub: Subscription;
  // A reload of the id on screen (after a Retry) rides the same switchMap as navigation, so a
  // late response can never overwrite a page the user has since moved to.
  private refresh$ = new Subject<void>();
  private currentParams: ParamMap;

  constructor(
    private auditService: FinanceService,
    private helpersService: HelpersService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private bannerNotificationsService: BannerNotificationsService,
    private drawer: MtxDrawer,
    private clipboard: Clipboard
  ) {}

  ngOnInit() {
    this.loading = true;
    this.userService
      .getAuthenticatedUser([AdminScopes.VIEW_FINANCE])
      .pipe(first())
      .subscribe(
        (adminUser: AdminUser) => {
          if (adminUser) {
            this.user = adminUser;
            this.canModifyFinance = this.userService.hasAuthScope(adminUser, [AdminScopes.MODIFY_FINANCE]);
            this.canViewUnknownTracking = this.userService.hasAuthScope(adminUser, [AdminScopes.VIEW_UNKNOWN_TRACKING]);
            this.watchRoute();
          } else {
            this.router.navigate(['login']);
          }
        },
        (err) => {
          this.router.navigate(['home']);
        }
      );
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
  }

  // Kept for the existing template bindings and specs: the header reads straight off the detail.
  public get invoice(): any {
    return this.detail?.invoice;
  }

  public get shipments(): any[] {
    return this.detail?.shipments ?? [];
  }

  public get totalShipments(): number {
    return this.detail?.totalShipments ?? 0;
  }

  public get isUber(): boolean {
    return this.detail?.invoice?.courier === SupportedReconProvider.UBER;
  }

  public get outcomeMessage(): string {
    return this.detail ? buildOutcomeMessage(this.detail) : '';
  }

  public get reconciledEmptyState(): string {
    return this.detail ? buildReconciledEmptyState(this.detail) : '';
  }

  public get statusText(): string {
    return getStatusCellText(this.detail?.invoice);
  }

  public get statusPillClass(): string {
    return PILL_CLASS_BY_STATUS[this.detail?.invoice?.status] ?? 'created';
  }

  public get identifierLabel(): string {
    return getIdentifierLabel(this.detail?.invoice?.courier);
  }

  public get isAirCanadaCargo(): boolean {
    return this.detail?.invoice?.courier === SupportedReconProvider.FREIGHT_AIR_CANADA;
  }

  public sectionNotice(section: InvoiceAuditSectionName): string | null {
    return getSectionNotice(this.detail, section);
  }

  public isSectionOk(section: InvoiceAuditSectionName): boolean {
    return getSectionState(this.detail, section) === 'ok';
  }

  public siblingStatusText(status: string): string {
    return getStatusCellText({ status });
  }

  public siblingPillClass(status: string): string {
    return PILL_CLASS_BY_STATUS[status] ?? 'created';
  }

  public get restrictedNote(): string {
    const open = this.detail?.summary?.reviewOpen ?? 0;
    return `${formatNumber(open, 'en-US')} ${open === 1 ? 'row is' : 'rows are'} open in Unknown Tracking.`;
  }

  // The backend caps what it hydrates/returns for this page at 500 (see admin.service's
  // INVOICE_DETAIL_LIST_LIMIT); totalShipments still carries the true count, so this only fires
  // once the reconciled list was actually truncated.
  public get reconciledTruncatedNote(): string | null {
    const total = this.totalShipments;
    const shown = this.shipments.length;
    if (total <= shown) {
      return null;
    }
    return `Showing the first ${formatNumber(shown, 'en-US')} of ${formatNumber(total, 'en-US')} reconciled shipments.`;
  }

  // Same cap for Unknown Tracking rows, only when the caller can see them at all — a restricted
  // caller already gets restrictedNote above, and unknownShipments is always [] for them, which
  // would otherwise read as "showing the first 0".
  public get unknownTruncatedNote(): string | null {
    if (this.detail?.unknownTrackingRestricted) {
      return null;
    }
    const open = this.detail?.summary?.reviewOpen;
    const shown = this.detail?.unknownShipments?.length ?? 0;
    if (open == null || open <= shown) {
      return null;
    }
    return `Showing the first ${formatNumber(shown, 'en-US')} of ${formatNumber(open, 'en-US')} rows — open Unknown Tracking to see them all.`;
  }

  private buildTiles(): CountTile[] {
    const summary = this.detail?.summary;
    const dash = (value: number | null | undefined): string => (value == null ? '—' : formatNumber(value, 'en-US'));
    const tile = (
      label: string,
      value: number | null | undefined,
      section: InvoiceAuditSectionName | null,
      liveValue: number | null | undefined,
      text: (n: string) => string
    ): CountTile => {
      const unavailable = !!summary && !!section && !this.isSectionOk(section);
      const subLine = unavailable
        ? "couldn't load"
        : summary && liveValue != null
          ? text(formatNumber(liveValue, 'en-US'))
          : null;
      return { label, value: dash(value), subLine, unavailable };
    };
    return [
      tile('Rows', summary?.rowsParsed, null, summary?.rowsSkipped || null, (n) => `${n} skipped`),
      tile('Reconciled', summary?.rowsDone, 'shipments', summary?.shipmentsFound, (n) => `shipments found: ${n}`),
      tile('Review', summary?.rowsReview, 'unknownShipments', summary?.reviewOpen, (n) => `${n} still open`),
      tile('Failed', summary?.rowsFailed, 'failedRows', summary?.failedOpen, (n) => `${n} open`),
    ];
  }

  public get reconciledTabLabel(): string {
    return `Reconciled shipments (${this.isSectionOk('shipments') ? formatNumber(this.totalShipments, 'en-US') : '?'})`;
  }

  public get unknownTabLabel(): string {
    const open = this.detail?.summary?.reviewOpen;
    return `Unknown Tracking (${this.isSectionOk('unknownShipments') && open != null ? formatNumber(open, 'en-US') : '?'} open)`;
  }

  public get failedTabLabel(): string {
    const logged = this.detail?.failedRows?.length ?? 0;
    return `Failed rows (${this.isSectionOk('failedRows') ? formatNumber(logged, 'en-US') : '?'})`;
  }

  // The failure log can trail the run count (write lag, a run before the log existed, or a failure
  // list over the finalize size caps); said neutrally rather than as an error. Only the log's OPEN
  // rows stand for the latest run's failures: a row a later run fixed stays in the log as Resolved,
  // and counting it would report e.g. "2 of 0" after a successful Retry.
  public get failedLogNote(): string | null {
    const summary = this.detail?.summary;
    if (!summary || !this.isSectionOk('failedRows') || summary.rowsFailed == null || summary.failedOpen == null) {
      return null;
    }
    return summary.failedOpen !== summary.rowsFailed
      ? `The failure log shows ${formatNumber(summary.failedOpen, 'en-US')} of ${formatNumber(summary.rowsFailed, 'en-US')} failed rows.`
      : null;
  }

  // Opens Unknown Tracking narrowed to this file (and BillID); regular review rows only.
  private buildUnknownTrackingQueryParams(): Record<string, string> {
    const invoice = this.detail?.invoice;
    return {
      courier: invoice?.courier,
      file: invoice?.file_name,
      ...(this.isUber && invoice?.invoice_number ? { billId: invoice.invoice_number } : {}),
    };
  }

  public onRetried(): void {
    this.reload();
  }

  public copyText(text: string): void {
    if (text && this.clipboard.copy(text)) {
      this.bannerNotificationsService.success('Copied to clipboard');
    }
  }

  public openUnknownRow(row: any): void {
    this.drawer.open(EdiInvoiceComponent, {
      width: PanelWidths.desktopFull,
      disableClose: true,
      closeOnNavigation: false,
      data: { shipment: row, type: EDI_INVOICE_MODAL_TYPE.UNKNOWN_TRACKING },
    });
  }

  public openRawRow(row: ReconAuditFailedRow): void {
    this.drawer.open(EdiInvoiceComponent, {
      width: PanelWidths.desktopFull,
      disableClose: true,
      closeOnNavigation: false,
      data: { shipment: { ediInvoice: [row.rawRow] }, type: EDI_INVOICE_MODAL_TYPE.EDI_INVOICE_ONLY },
    });
  }

  public reviewReason(row: any): string {
    const reason = row?.data?.reviewReason;
    return typeof reason === 'string' && reason.trim() ? reason : '—';
  }

  public isDoNotBill(row: any): boolean {
    return row?.type === 'D';
  }

  // After a Retry the row flips to processing; refetch it so the header, banner and Retry agree.
  private reload(): void {
    this.refresh$.next();
  }

  // The same component instance serves every id on the route, so each param change must reload.
  private watchRoute(): void {
    const navigations = this.route.paramMap.pipe(
      tap((params) => {
        this.currentParams = params;
        this.resetState();
      })
    );
    const refreshes = this.refresh$.pipe(map(() => this.currentParams));
    this.routeSub = merge(navigations, refreshes)
      .pipe(
        switchMap((params: ParamMap) =>
          this.loadInvoice(params).pipe(
            catchError((err: Error) => {
              this.bannerNotificationsService.error(err.message, {
                category: 'GET courier invoice shipments data error',
                message: err,
              });
              this.loading = false;
              return EMPTY;
            })
          )
        )
      )
      .subscribe((detail) => this.setDetail(detail));
  }

  private loadInvoice(params: ParamMap): Observable<CourierInvoiceAuditDetail> {
    const id = params?.get('id');
    if (id) {
      return this.auditService.getCourierInvoiceAuditDetail(Number(id));
    }
    this.invoiceName = params?.get('invoiceName');
    return this.auditService.getCourierInvoiceShipments(this.invoiceName).pipe(map((res) => this.toLegacyDetail(res)));
  }

  // The bookmark route returns the header and shipments only; it has no row-level outcomes.
  private toLegacyDetail(res: any): CourierInvoiceAuditDetail {
    return {
      legacy: true,
      invoice: res?.invoice,
      trackingAvailable: false,
      siblings: [],
      summary: null,
      shipments: res?.shipments || [],
      totalShipments: res?.totalShipments || 0,
      unknownShipments: [],
      unknownTrackingRestricted: !this.canViewUnknownTracking,
      failedRows: [],
    };
  }

  private setDetail(detail: CourierInvoiceAuditDetail): void {
    if (detail?.invoice) {
      detail.invoice.providerName = this.helpersService.convertSupportedReconProvidersEnumToString(
        detail.invoice.courier as SupportedReconProvider
      );
    }
    this.detail = detail;
    this.tiles = this.buildTiles();
    this.unknownTrackingQueryParams = this.buildUnknownTrackingQueryParams();
    this.unknownDataSource.data = detail?.unknownShipments ?? [];
    this.failedDataSource.data = detail?.failedRows ?? [];
    this.loading = false;
  }

  private resetState(): void {
    this.loading = true;
    this.detail = null;
    this.tiles = [];
    this.unknownTrackingQueryParams = {};
    this.invoiceName = undefined;
    this.unknownDataSource.data = [];
    this.failedDataSource.data = [];
  }
}
